/**
 * `Settings` shell — ported from `Foyer_Dashboard_CURRENT.jsx` line 7526.
 *
 * Five rail sections (2026-10-01): General, Install, Skills, Integrations,
 * Origins. Install absorbs the former Endpoint tab's content (minus the
 * Re-scan schedule, now on the Agent Readiness page header) plus the
 * install-related pieces that used to live inside Integrations
 * (InstallCoverageCard + Cloudflare Worker deploy + manual Worker /
 * plugin downloads). The former Scan and AI Discovery tabs were removed:
 * their content is on the Agent Readiness page.
 *
 * Deep-link aliases:
 *   - `settings_tab=endpoint` → `install` (legacy id, redirected here)
 *   - `settings_tab=scan`     → routed to the Agent Readiness view by
 *     `initial-view.ts` BEFORE Settings mounts.
 *   - `settings_tab=llms_txt` → routed to the Agent Readiness view with
 *     the llms.txt drawer open on mount.
 */

import {
  Bell,
  Globe,
  Link2,
  Server,
  Settings as SettingsIcon,
  Sparkles,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { useSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { NumLabel } from '../../components/atoms/index.js';
import { SettingsGeneral } from './SettingsGeneral.js';
import { SettingsInstall } from './SettingsInstall.js';
import { SettingsIntegrations } from './SettingsIntegrations.js';
import { SettingsNotifications } from './SettingsNotifications.js';
import { SettingsOrigins } from './SettingsOrigins.js';
import { SettingsSkills } from './SettingsSkills.js';

const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type SectionId =
  | 'general'
  | 'install'
  | 'skills'
  | 'integrations'
  | 'origins'
  | 'notifications';

interface SectionDef {
  id: SectionId;
  label: string;
  icon: typeof SettingsIcon;
}

// Founder 2026-10-20: Blocking moved out of Settings and under the
// top-level Security tab (`?view=security&section=blocking|paths`).
// The old deep link (`?view=settings&settings_section=blocking` ±
// `&blocking_tab=paths`) is redirected by `initialView()` so email
// bookmarks keep working.
const SECTIONS: readonly SectionDef[] = [
  { id: 'general', label: 'General', icon: SettingsIcon },
  { id: 'install', label: 'Install', icon: Server },
  { id: 'skills', label: 'Skills', icon: Sparkles },
  { id: 'integrations', label: 'Integrations', icon: Link2 },
  { id: 'origins', label: 'Origins', icon: Globe },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

// Sections the deep-link channel (`?settings_tab=<id>`) may target. Every
// rail section is a valid target now; an unknown value is ignored.
const DEEP_LINK_SECTIONS: ReadonlySet<SectionId> = new Set(
  SECTIONS.map((s) => s.id),
);

/**
 * Read the optional `?settings_tab=<id>` deep-link target from the URL.
 * The SiteCard's "Review →" band routes here by setting this param +
 * navigating to `view=settings`. We narrow to known sections and ignore
 * anything else so a stale link never leaves the user on a blank page.
 */
function readDeepLinkSection(): SectionId | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('settings_tab');
    if (raw === null) return null;
    // Legacy id aliasing. The old `endpoint` tab's content now lives on
    // Install, so an existing bookmark lands there.
    const aliased = raw === 'endpoint' ? 'install' : raw;
    if (DEEP_LINK_SECTIONS.has(aliased as SectionId)) {
      return aliased as SectionId;
    }
  } catch {
    // URL parsing failed in an exotic environment — fall through to
    // the default 'general' section.
  }
  return null;
}

/**
 * Read the optional `?settings_section=<anchor>` deep-link target. The
 * value is a stable `id` on a block inside the tab — Settings scrolls
 * it into view + briefly outlines it so the merchant lands on the
 * exact row they followed the link to.
 *
 * Allow-list the known anchors so a stale link cannot leak into the
 * DOM query. Add new anchors here as new blocks become link targets.
 */
const KNOWN_SETTINGS_ANCHORS: ReadonlySet<string> = new Set([
  // Install tab
  'how-installed', 'install', 'endpoint', 'protocol-support',
  // Install tab — the "Update available" callout, deep-linked from the
  // Sites card update band (founder 2026-10-27).
  'install-update',
  // Skills tab
  'skills-list',
  // Integrations tab — Slack card. Deep-linked from the Notifications
  // page's "Connect Slack →" and "Pause all" affordances so the
  // merchant lands on the right card instead of the top of the tab
  // (founder bug 2026-10-20 Report 2).
  'slack',
  // Install tab — Cloudflare Worker card. Deep-linked from Security ›
  // Blocking when the site has no Worker to block with (founder
  // 2026-10-27).
  'cloudflare',
]);

function readDeepLinkAnchor(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = new URLSearchParams(window.location.search).get('settings_section');
    if (raw === null) return null;
    return KNOWN_SETTINGS_ANCHORS.has(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function Settings(): JSX.Element | null {
  const C = useC();
  const { activeSite } = useSites();
  const [section, setSection] = useState<SectionId>(() => {
    // Founder 2026-10-20: `settings_section=blocking` + `=install_blocking`
    // are redirected to the Security view by `initialView()` before this
    // component mounts, so we no longer handle them here.
    const direct = readDeepLinkSection();
    if (direct !== null) return direct;
    return 'general';
  });
  const [anchor, setAnchor] = useState<string | null>(() => readDeepLinkAnchor());
  const [highlightAnchor, setHighlightAnchor] = useState<string | null>(null);

  // Consume the deep-link params once so a manual tab change doesn't
  // get overwritten by a stale URL value on remount. We strip
  // `settings_tab` + `settings_section` from the URL after the initial
  // section is set; the `view=settings` param the App owns is
  // preserved.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      let changed = false;
      if (url.searchParams.has('settings_tab')) {
        url.searchParams.delete('settings_tab');
        changed = true;
      }
      if (url.searchParams.has('settings_section')) {
        url.searchParams.delete('settings_section');
        changed = true;
      }
      if (changed) window.history.replaceState({}, '', url.toString());
    } catch {
      // Best-effort URL hygiene; safe to no-op on failure.
    }
  }, []);

  // Scroll the deep-link anchor into view + outline it. We wait a frame
  // so the tab content has mounted before `getElementById` runs. The
  // outline lasts ~1800 ms and is strictly ornamental.
  useEffect(() => {
    if (anchor === null) return;
    if (typeof document === 'undefined') return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      const el = document.getElementById(anchor);
      if (el !== null) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        catch { el.scrollIntoView(); }
        setHighlightAnchor(anchor);
        window.setTimeout(() => {
          if (!cancelled) setHighlightAnchor(null);
        }, 1800);
      }
      setAnchor(null);
    }, 60);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [anchor, section]);

  if (!activeSite) return null;

  // The anchor highlight is a plain CSS outline, applied via a scoped
  // style tag so sub-tab components do not need to know the current
  // anchor. 2px lime-ish outline + 2px offset — matches the thin-border
  // design language and is calm enough not to shout for 1800 ms.
  const anchorStyleTag = highlightAnchor !== null ? (
    <style>{`#${highlightAnchor} { outline: 2px solid ${C.lime}; outline-offset: 4px; border-radius: 4px; transition: outline-color 300ms ease; }`}</style>
  ) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      {anchorStyleTag}
      <div className="mb-8">
        <NumLabel n={1}>{activeSite.domain} · settings</NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(36px, 6vw, 52px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          Make this site <em>yours</em>.
        </h1>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:flex-shrink-0" style={{ width: '100%', maxWidth: 200 }}>
          <div className="md:sticky md:top-24 space-y-1">
            {SECTIONS.map((s) => {
              const sel = section === s.id;

              return (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => setSection(s.id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                  style={{
                    background: sel ? C.surface2 : 'transparent',
                    color: sel ? C.text : C.muted,
                    border: `1px solid ${sel ? C.border : 'transparent'}`,
                    borderRadius: 2,
                    fontFamily: sansFont,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  <s.icon size={13} color={sel ? C.text : C.muted} />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {section === 'general' && <SettingsGeneral site={activeSite} />}
          {section === 'install' && <SettingsInstall site={activeSite} />}
          {section === 'skills' && <SettingsSkills />}
          {section === 'integrations' && (
            <SettingsIntegrations site={activeSite} />
          )}
          {section === 'origins' && <SettingsOrigins site={activeSite} />}
          {section === 'notifications' && (
            <SettingsNotifications site={activeSite} />
          )}
        </div>
      </div>
    </div>
  );
}

